import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Link, NavLink } from "react-router-dom";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Menu", to: "/menu" },
  { label: "About", to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "Merch", to: "/merch" },
  { label: "Reviews", to: "/reviews" },
  { label: "Visit", to: "/visit" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm uppercase tracking-widest font-semibold transition-colors ${
      isActive ? "text-[#E60000]" : "text-white/80 hover:text-[#E60000]"
    }`;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#333]" : "bg-[#0A0A0A]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <img
            src="/assets/hillboyz-logo.png"
            alt="HillBoyz logo"
            className="w-10 h-10 md:w-12 md:h-12 object-contain"
          />
          <div className="hb-logo-text text-3xl md:text-4xl">
            HILL<span className="accent">BOYZ</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={linkClass}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative bg-[#E60000] hover:bg-[#FF3333] text-white px-4 md:px-5 h-10 md:h-11 font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors border-2 border-transparent hover:border-white"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">Merch</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="bg-black text-white text-xs font-bold px-2 py-0.5 ml-1">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0A0A0A] border-t border-[#333]">
          <div className="px-4 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `py-3 px-2 uppercase tracking-widest font-semibold border-b border-[#222] ${
                    isActive ? "text-[#E60000]" : "text-white/90 hover:text-[#E60000]"
                  }`
                }
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
